"use client";

import { useEffect, useRef } from "react";

import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { TOP_MENU_BUTTON_OPEN_CLASS_NAME } from "@/components/doc/left-sidebar/top-menu/topMenuButtonStyles";
import { UnreadCountBadge } from "@/components/doc/left-sidebar/top-menu/UnreadCountBadge";
import { useSidebarUnreadCounts } from "@/components/doc/left-sidebar/top-menu/useSidebarUnreadCounts";
import { Icon } from "@/components/icons/Icon";
import { Sidebar } from "@/components/sidebar";
import { useStore } from "@/store";
import { twMerge } from "@/utils/twMerge";

import { PersonalDocumentTree } from "./PersonalDocumentTree";

function PreserveWindowScroll({ children }: { children: React.ReactNode }) {
  const savedScrollY = useRef(typeof window !== "undefined" ? window.scrollY : 0);

  useEffect(() => {
    window.scrollTo({ top: savedScrollY.current, behavior: "instant" });
  }, []);

  return children;
}

export const PersonalDocumentTreePopover = observer(function PersonalDocumentTreePopover() {
  const { documentTree } = useStore();
  const { docsUnread } = useSidebarUnreadCounts();
  const t = useTranslations("documentsSidebar");

  if (!documentTree.root) {
    return null;
  }

  return (
    <Popover className="relative flex w-full min-h-0">
      {({ open }) => (
        <>
          <PopoverButton
            as={Sidebar.Button}
            icon={
              <span className="relative flex">
                <Icon name="Docs" />
                <UnreadCountBadge
                  count={docsUnread}
                  label={`${docsUnread} unread document messages`}
                />
              </span>
            }
            title={t("documentTreePanel")}
            className={twMerge(open && TOP_MENU_BUTTON_OPEN_CLASS_NAME)}
          />
          <PopoverPanel
            portal
            anchor={{ to: "right start", gap: 14, padding: 10 }}
            className={twMerge(
              "z-100 flex min-h-0 flex-col overflow-hidden rounded-12",
              "bg-block-bg shadow-md dark:shadow-block-inverted",
              "outline-none",
              "w-sidebar-width min-w-sidebar-width max-h-[min(70vh,calc(100vh-3rem))]",
            )}
          >
            <PreserveWindowScroll>
              <PersonalDocumentTree />
            </PreserveWindowScroll>
          </PopoverPanel>
        </>
      )}
    </Popover>
  );
});
